import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSingleton } from "../../hooks/use-singleton";
import { useApi } from "../../hooks/use-api";
import { useNotify } from "../../hooks/use-notify";
import {
  apiTestCategories,
  apiTestExams,
  apiTestClasses,
  apiTestChapters,
  apiTestSubTopics,
  API_CREATE_TEACHER_TEST,
  apiTestList,
  apiTeacherTestByName,
} from "../../utils/api-routes";

export const parseDurationInMinutes = (val, unit = "mins") => {
  if (val === null || val === undefined) return 0;
  const str = String(val).trim().toLowerCase();
  if (!str) return 0;

  const isHours = unit === "hours" || unit === "hrs" || str.includes("hr") || str.includes("hour");
  const match = str.match(/([0-9]+(\.[0-9]+)?)/);
  if (!match) return 0;

  const num = parseFloat(match[1]);
  if (isNaN(num)) return 0;

  if (isHours) {
    return Math.round(num * 60);
  }
  return Math.round(num);
};

export const useHandler = () => {
  const navigate = useNavigate();
  const { user, mail, role } = useSingleton();
  const { get, post, put, del } = useApi();
  const { toggleNotify } = useNotify();

  const teacherMail = mail || user?.data?.email;
  const teacherRole = role || user?.data?.role;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewMode, setViewMode] = useState("selection"); // "selection" | "form" | "existing"
  const [openDropdown, setOpenDropdown] = useState(null); // 'category' | 'exam' | 'class' | 'chapter' | 'subtopic' | null
  const [dateFocused, setDateFocused] = useState(false);
  const [timeFocused, setTimeFocused] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalTests, setTotalTests] = useState(0);
  const pageSize = 8;

  const [existingTests, setExistingTests] = useState([]);
  const [editingTestName, setEditingTestName] = useState(null);
  const [deleteModalTestName, setDeleteModalTestName] = useState(null);
  const [viewingTest, setViewingTest] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const formRef = useRef(null);

  // Form State
  const [formData, setFormData] = useState({
    testName: "",
    testCategory: "",
    exam: "",
    className: "",
    chapters: [],
    subTopic: [],
    date: "",
    time: "",
    duration: "",
    durationUnit: "mins",
  });

  // Dropdown options states
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [examOptions, setExamOptions] = useState([]);
  const [classOptions, setClassOptions] = useState([]);
  const [chapterOptions, setChapterOptions] = useState([]);
  const [subTopicOptions, setSubTopicOptions] = useState([]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (formRef.current && !formRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Filter state for Existing Tests table
  const [tableFilters, setTableFilters] = useState({
    testName: "",
    testCategory: "",
    exam: "",
    className: "",
    chapter: "",
    subTopic: "",
    date: "",
  });

  const [openFilterDropdown, setOpenFilterDropdown] = useState(null);
  const filterBarRef = useRef(null);

  // Close filter dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterBarRef.current && !filterBarRef.current.contains(event.target)) {
        setOpenFilterDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const [debouncedTestName, setDebouncedTestName] = useState("");

  // Debounce testName input to avoid making API calls on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTestName(tableFilters.testName);
    }, 400);
    return () => clearTimeout(timer);
  }, [tableFilters.testName]);

  const [filterClassOptions, setFilterClassOptions] = useState([]);
  const [filterChapterOptions, setFilterChapterOptions] = useState([]);
  const [filterSubTopicOptions, setFilterSubTopicOptions] = useState([]);

  // Fetch existing tests list from API
  useEffect(() => {
    const fetchExistingTests = async () => {
      if (!teacherMail) return;
      try {
        const queryFilters = { ...tableFilters, testName: debouncedTestName };
        const res = await get(
          apiTestList(teacherMail, teacherRole, currentPage, pageSize, queryFilters)
        );
        const data = res?.data || res || {};
        const tests = data.tests || (Array.isArray(data) ? data : []);
        setTotalTests(data.total || tests.length || 0);

        if (Array.isArray(tests) && tests.length > 0) {
          const mapped = tests.map((t) => ({
            testName: t.test_name || t.testName || "Assessment Test",
            subject: t.subject || "Physics",
            testCategory: t.test_category || t.testCategory || "",
            examType: t.exam || t.exam_type || t.examType || "NEET",
            className: t.class || t.class_name || t.className || "Class 12",
            chapter: t.chapter || "General Chapter",
            subTopic: t.sub_topic || t.subTopic || "Topic Assessment",
            date:
              t.test_date ||
              (t.created_at ? t.created_at.split("T")[0] : ""),
            time:
              t.test_time ||
              (t.created_at
                ? t.created_at.split("T")[1]?.slice(0, 5)
                : ""),
            durationMinutes:
              t.duration_minutes !== undefined
                ? t.duration_minutes
                : t.durationMinutes || null,
          }));
          setExistingTests(mapped);
        } else {
          setExistingTests([]);
        }
      } catch (e) {
        console.log("Error fetching existing tests list", e);
      }
    };
    fetchExistingTests();
  }, [
    currentPage,
    viewMode,
    teacherMail,
    teacherRole,
    refreshKey,
    debouncedTestName,
    tableFilters.testCategory,
    tableFilters.exam,
    tableFilters.className,
    tableFilters.chapter,
    tableFilters.subTopic,
    tableFilters.date,
  ]);

  // Fetch test categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      if (!teacherMail) return;
      try {
        const res = await get(apiTestCategories(teacherMail, teacherRole));
        const list =
          res?.data?.categories ||
          res?.categories ||
          (Array.isArray(res?.data) ? res.data : []);
        setCategoryOptions(Array.isArray(list) ? list : []);
      } catch (e) {
        console.log("Error fetching test categories", e);
        setCategoryOptions([]);
      }
    };
    fetchCategories();
  }, [teacherMail, teacherRole]);

  // Fetch test exams from API
  useEffect(() => {
    const fetchExams = async () => {
      if (!teacherMail) return;
      try {
        const res = await get(apiTestExams(teacherMail, teacherRole));
        const list =
          res?.data?.exams ||
          res?.exams ||
          (Array.isArray(res?.data) ? res.data : []);
        setExamOptions(Array.isArray(list) ? list : []);
      } catch (e) {
        console.log("Error fetching test exams", e);
        setExamOptions([]);
      }
    };
    fetchExams();
  }, [teacherMail, teacherRole]);

  // Fetch test classes based on selected exam from API
  useEffect(() => {
    const fetchClasses = async () => {
      if (!formData.exam || !teacherMail) {
        setClassOptions([]);
        return;
      }
      const selectedExam = formData.exam;
      try {
        const res = await get(
          apiTestClasses(selectedExam, teacherMail, teacherRole)
        );
        const list =
          res?.data?.classes ||
          res?.classes ||
          (Array.isArray(res?.data) ? res.data : []);
        setClassOptions(Array.isArray(list) ? list : []);
      } catch (e) {
        console.log("Error fetching test classes", e);
        setClassOptions([]);
      }
    };
    fetchClasses();
  }, [formData.exam, teacherMail, teacherRole]);

  // Fetch test chapters based on selected exam and class from API
  useEffect(() => {
    const fetchChapters = async () => {
      if (!formData.exam || !formData.className || !teacherMail) {
        setChapterOptions([]);
        return;
      }
      const selectedExam = formData.exam;
      const selectedClass = formData.className;
      try {
        const res = await get(
          apiTestChapters(selectedExam, selectedClass, teacherMail, teacherRole)
        );
        const list =
          res?.data?.chapters ||
          res?.chapters ||
          (Array.isArray(res?.data) ? res.data : []);
        setChapterOptions(Array.isArray(list) ? list : []);
      } catch (e) {
        console.log("Error fetching test chapters", e);
        setChapterOptions([]);
      }
    };
    fetchChapters();
  }, [formData.exam, formData.className, teacherMail, teacherRole]);

  // Fetch test sub-topics based on selected exam and chapter from API
  useEffect(() => {
    const fetchSubTopics = async () => {
      if (!formData.chapters || formData.chapters.length === 0 || !teacherMail) {
        setSubTopicOptions([]);
        return;
      }
      const selectedExam = formData.exam || "";
      try {
        const res = await get(
          apiTestSubTopics(
            selectedExam,
            formData.chapters,
            teacherMail,
            teacherRole
          )
        );
        const list =
          res?.data?.sub_topics ||
          res?.sub_topics ||
          (Array.isArray(res?.data) ? res.data : []);
        setSubTopicOptions(Array.isArray(list) ? list : []);
      } catch (e) {
        console.log("Error fetching test sub-topics", e);
        setSubTopicOptions([]);
      }
    };
    fetchSubTopics();
  }, [formData.exam, formData.chapters, teacherMail, teacherRole]);

  // Actions
  const toggleDropdown = (dropdownName) => {
    setOpenDropdown((prev) => (prev === dropdownName ? null : dropdownName));
  };

  const handleChapterToggle = (chapterTitle) => {
    setFormData((prev) => {
      const exists = prev.chapters.includes(chapterTitle);
      const updatedChapters = exists
        ? prev.chapters.filter((item) => item !== chapterTitle)
        : [...prev.chapters, chapterTitle];
      return { ...prev, chapters: updatedChapters };
    });
  };

  const handleSubTopicToggle = (subTopicTitle) => {
    setFormData((prev) => {
      const currentSubTopics = Array.isArray(prev.subTopic) ? prev.subTopic : [];
      const exists = currentSubTopics.includes(subTopicTitle);
      const updatedSubTopics = exists
        ? currentSubTopics.filter((item) => item !== subTopicTitle)
        : [...currentSubTopics, subTopicTitle];
      return { ...prev, subTopic: updatedSubTopics };
    });
  };

  const handleResetForm = () => {
    setEditingTestName(null);
    setFormData({
      testName: "",
      testCategory: "",
      exam: "",
      className: "",
      chapters: [],
      subTopic: [],
      date: "",
      time: "",
      duration: "",
      durationUnit: "mins",
    });
    setOpenDropdown(null);
  };

  const isFormValid = Boolean(
    formData.testName?.trim() &&
      formData.testCategory &&
      formData.exam &&
      formData.className &&
      (Array.isArray(formData.chapters)
        ? formData.chapters.length > 0
        : Boolean(formData.chapters && String(formData.chapters).trim())) &&
      formData.date &&
      formData.time &&
      formData.duration &&
      String(formData.duration).trim() !== "" &&
      parseDurationInMinutes(formData.duration, formData.durationUnit) > 0
  );

  const handleCreateTestSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid) return;
    setIsSubmitting(true);

    const durationMinutes = parseDurationInMinutes(
      formData.duration,
      formData.durationUnit
    );

    const payload = {
      email: teacherMail,
      role: teacherRole,
      test_name: formData.testName,
      test_category: formData.testCategory,
      exam: formData.exam,
      class: formData.className,
      chapter: Array.isArray(formData.chapters)
        ? formData.chapters.join(",")
        : (formData.chapters || ""),
      sub_topic: Array.isArray(formData.subTopic)
        ? formData.subTopic.join(",")
        : (formData.subTopic || ""),
      test_date: formData.date,
      test_time:
        formData.time && formData.time.length === 5
          ? `${formData.time}:00`
          : formData.time,
      duration_minutes: durationMinutes,
    };

    try {
      if (editingTestName) {
        await put(
          apiTeacherTestByName(editingTestName, teacherMail, teacherRole),
          payload
        );
      } else {
        await post(API_CREATE_TEACHER_TEST, payload);
      }
      setIsSubmitting(false);

      if (toggleNotify) {
        toggleNotify({
          type: "success",
          title: editingTestName ? "Test Updated" : "Test Created",
          description: editingTestName
            ? "Test updated successfully!"
            : "Test created successfully!",
        });
      }

      // Reset form fields and editing state
      setEditingTestName(null);
      setFormData({
        testName: "",
        testCategory: "",
        exam: "",
        className: "",
        chapters: [],
        subTopic: [],
        date: "",
        time: "",
        duration: "",
        durationUnit: "mins",
      });

      // Switch view to Existing Tests list
      setViewMode("existing");
      setRefreshKey((prev) => prev + 1);
    } catch (e) {
      console.log("Error saving test", e);
      setIsSubmitting(false);
    }
  };

  const handleFetchSingleTest = async (testName, mode = "view") => {
    try {
      const res = await get(
        apiTeacherTestByName(testName, teacherMail, teacherRole)
      );
      const testData = res?.data || res || {};

      if (mode === "edit") {
        setEditingTestName(testName);
        let chapterList = [];
        if (testData.chapter) {
          if (Array.isArray(testData.chapter)) {
            chapterList = testData.chapter;
          } else if (typeof testData.chapter === "string") {
            chapterList = testData.chapter.split(",");
          }
        }
        let subTopicList = [];
        if (testData.sub_topic) {
          if (Array.isArray(testData.sub_topic)) {
            subTopicList = testData.sub_topic;
          } else if (typeof testData.sub_topic === "string") {
            subTopicList = testData.sub_topic.split(",");
          }
        }

        setFormData({
          testName: testData.test_name || testName,
          testCategory: testData.test_category || "",
          exam: testData.exam || "",
          className: testData.class || "",
          chapters: chapterList,
          subTopic: subTopicList,
          date: testData.test_date || "",
          time: testData.test_time ? testData.test_time.slice(0, 5) : "",
          duration: testData.duration_minutes ? String(testData.duration_minutes) : "",
          durationUnit: "mins",
        });
        setViewMode("form");
      } else {
        setViewingTest(testData);
      }
    } catch (e) {
      console.log("Error fetching test details", e);
      if (toggleNotify) {
        toggleNotify({
          type: "error",
          title: "Error",
          description: "Failed to fetch test details.",
        });
      }
    }
  };

  const handleDeleteTestConfirm = async () => {
    if (!deleteModalTestName) return;
    try {
      await del(
        apiTeacherTestByName(deleteModalTestName, teacherMail, teacherRole)
      );
      if (toggleNotify) {
        toggleNotify({
          type: "success",
          title: "Test Deleted",
          description: `Test "${deleteModalTestName}" deleted successfully!`,
        });
      }
      setDeleteModalTestName(null);
      setRefreshKey((prev) => prev + 1);
    } catch (e) {
      console.log("Error deleting test", e);
      if (toggleNotify) {
        toggleNotify({
          type: "error",
          title: "Error",
          description: "Failed to delete test.",
        });
      }
    }
  };

  const getInitials = (name) => {
    if (!name) return "T";
    return name.substring(0, 2).toUpperCase();
  };

  // Fetch test classes for table filters
  useEffect(() => {
    const fetchFilterClasses = async () => {
      if (!teacherMail || !tableFilters.exam) {
        setFilterClassOptions([]);
        return;
      }
      try {
        const res = await get(
          apiTestClasses(tableFilters.exam, teacherMail, teacherRole)
        );
        const list =
          res?.data?.classes ||
          res?.classes ||
          (Array.isArray(res?.data) ? res.data : []);
        setFilterClassOptions(Array.isArray(list) ? list : []);
      } catch (e) {
        console.log("Error fetching filter classes", e);
        setFilterClassOptions([]);
      }
    };
    fetchFilterClasses();
  }, [tableFilters.exam, teacherMail, teacherRole]);

  // Fetch test chapters for table filters
  useEffect(() => {
    const fetchFilterChapters = async () => {
      if (!teacherMail || !tableFilters.className) {
        setFilterChapterOptions([]);
        return;
      }
      try {
        const res = await get(
          apiTestChapters(
            tableFilters.exam || "",
            tableFilters.className || "",
            teacherMail,
            teacherRole
          )
        );
        const list =
          res?.data?.chapters ||
          res?.chapters ||
          (Array.isArray(res?.data) ? res.data : []);
        setFilterChapterOptions(Array.isArray(list) ? list : []);
      } catch (e) {
        console.log("Error fetching filter chapters", e);
        setFilterChapterOptions([]);
      }
    };
    fetchFilterChapters();
  }, [tableFilters.exam, tableFilters.className, teacherMail, teacherRole]);

  // Fetch test sub-topics for table filters
  useEffect(() => {
    const fetchFilterSubTopics = async () => {
      if (!teacherMail || !tableFilters.chapter) {
        setFilterSubTopicOptions([]);
        return;
      }
      try {
        const res = await get(
          apiTestSubTopics(
            tableFilters.exam || "",
            tableFilters.chapter || "",
            teacherMail,
            teacherRole
          )
        );
        const list =
          res?.data?.sub_topics ||
          res?.sub_topics ||
          (Array.isArray(res?.data) ? res.data : []);
        setFilterSubTopicOptions(Array.isArray(list) ? list : []);
      } catch (e) {
        console.log("Error fetching filter sub-topics", e);
        setFilterSubTopicOptions([]);
      }
    };
    fetchFilterSubTopics();
  }, [tableFilters.exam, tableFilters.chapter, teacherMail, teacherRole]);

  // Filter actions for Existing Tests table
  const handleTableFilterChange = (key, value) => {
    setTableFilters((prev) => {
      const updated = { ...prev, [key]: value };
      if (key === "exam") {
        updated.className = "";
        updated.chapter = "";
        updated.subTopic = "";
      }
      if (key === "className") {
        updated.chapter = "";
        updated.subTopic = "";
      }
      if (key === "chapter") {
        updated.subTopic = "";
      }
      return updated;
    });
    setCurrentPage(1);
  };

  const handleClearTableFilters = () => {
    setTableFilters({
      testName: "",
      testCategory: "",
      exam: "",
      className: "",
      chapter: "",
      subTopic: "",
      date: "",
    });
    setCurrentPage(1);
  };

  const hasActiveFilters = Object.values(tableFilters).some((val) => Boolean(val));

  const allTestNameOptions = Array.from(
    new Set(existingTests.map((t) => t.testName || t.test_name).filter(Boolean))
  );

  const allCategoryOptions = Array.from(
    new Set([
      ...categoryOptions,
      ...existingTests.map((t) => t.testCategory).filter(Boolean),
    ])
  );

  const allExamOptions = Array.from(
    new Set([
      ...examOptions,
      ...existingTests.map((t) => t.examType).filter(Boolean),
    ])
  );

  const allClassOptions = filterClassOptions;
  const allChapterOptions = filterChapterOptions;
  const allSubTopicOptions = filterSubTopicOptions;

  const displayedTests = existingTests.filter((item) => {
    if (
      tableFilters.testName &&
      !item.testName.toLowerCase().includes(tableFilters.testName.toLowerCase())
    ) {
      return false;
    }
    if (
      tableFilters.testCategory &&
      item.testCategory &&
      item.testCategory.toLowerCase() !== tableFilters.testCategory.toLowerCase()
    ) {
      return false;
    }
    if (
      tableFilters.exam &&
      item.examType &&
      item.examType.toLowerCase() !== tableFilters.exam.toLowerCase() &&
      !item.examType.toLowerCase().includes(tableFilters.exam.toLowerCase())
    ) {
      return false;
    }
    if (
      tableFilters.className &&
      item.className &&
      item.className.toLowerCase() !== tableFilters.className.toLowerCase() &&
      !item.className.toLowerCase().includes(tableFilters.className.toLowerCase())
    ) {
      return false;
    }
    if (
      tableFilters.chapter &&
      item.chapter &&
      !item.chapter.toLowerCase().includes(tableFilters.chapter.toLowerCase())
    ) {
      return false;
    }
    if (
      tableFilters.subTopic &&
      item.subTopic &&
      !item.subTopic.toLowerCase().includes(tableFilters.subTopic.toLowerCase())
    ) {
      return false;
    }
    if (
      tableFilters.date &&
      item.date &&
      !item.date.includes(tableFilters.date)
    ) {
      return false;
    }
    return true;
  });

  return {
    navigate,
    user,
    mail,
    role,
    isSubmitting,
    viewMode,
    setViewMode,
    openDropdown,
    setOpenDropdown,
    dateFocused,
    setDateFocused,
    timeFocused,
    setTimeFocused,
    currentPage,
    setCurrentPage,
    totalTests,
    pageSize,
    existingTests,
    editingTestName,
    deleteModalTestName,
    setDeleteModalTestName,
    viewingTest,
    setViewingTest,
    formRef,
    formData,
    setFormData,
    categoryOptions,
    examOptions,
    classOptions,
    chapterOptions,
    subTopicOptions,
    toggleDropdown,
    handleChapterToggle,
    handleSubTopicToggle,
    handleResetForm,
    isFormValid,
    handleCreateTestSubmit,
    handleFetchSingleTest,
    handleDeleteTestConfirm,
    getInitials,
    tableFilters,
    handleTableFilterChange,
    handleClearTableFilters,
    hasActiveFilters,
    allTestNameOptions,
    allCategoryOptions,
    allExamOptions,
    allClassOptions,
    allChapterOptions,
    allSubTopicOptions,
    displayedTests,
    openFilterDropdown,
    setOpenFilterDropdown,
    filterBarRef,
  };
};

export default useHandler;
